import { EventMeta } from '../../../interfaces/meta.interface';

type FileDownloadedFields = {
    ObjectId: string;
};

const FileDownloaded: EventMeta<FileDownloadedFields> = {
    title: 'SharePoint File Downloaded',
    default: {
        description: 'SharePoint File Downloaded',
        longDescription: 'A user downloaded a file.',
        severity: 'info',
    },
    fieldsExtractor: (log: any) => {
        return {
            ObjectId: log.ObjectId,
        };
    },
    classifications: [
        // file downloaded: fraudulent IP. not in allowed list.
        {
            severity: 'danger',
            description: (fields) => `A suspicious IP downloaded a OneDrive file`,
            longDescription: (fields) =>
                `A file was downloaded from OneDrive from a suspicious IP address that is not in your allowed list. This activity may indicate an attempt to access or exfiltrate sensitive information without authorization.`,
            predicates: {
                isAllowedASN: false,
                isAllowedCountry: false,
                isAllowedRegion: false,
                isAllowedIP: false,
                isFraudulentIP: true,
                isMicrosoftActivity: false,
            },
        },
        // file downloaded: fraudulent IP.  allowed IP
        {
            severity: 'warning',
            description: (fields) => `A suspicious IP within your allowed IP list downloaded a file`,
            longDescription: (fields) =>
                `A file was downloaded from OneDrive. The action originated from a suspicious IP that belongs to your allowed IP list. While the IP range is normally trusted, the individual IP has been flagged as suspicious, making this activity notable. Review the download details to confirm whether the activity was expected or potentially unauthorized, particularly if the file contains sensitive content or if similar downloads recur from flagged IPs within trusted ranges.`,
            predicates: {
                isAllowedIP: true,
                isFraudulentIP: true,
                isMicrosoftActivity: false,
            },
        },
        // file downloaded: fraudulent IP. allowed region.
        {
            severity: 'warning',
            description: (fields) => `A suspicious IP within your allowed region downloaded a file`,
            longDescription: (fields) =>
                `A file was downloaded from OneDrive. The action originated from a suspicious IP located within your allowed region. Although the geographic location aligns with your policy, the individual IP has been flagged as suspicious, making the activity unusual. Review the download to confirm whether the access was legitimate, especially if the file is sensitive or if similar activity is observed from flagged IPs.`,
            predicates: {
                isAllowedRegion: true,
                isFraudulentIP: true,
                isMicrosoftActivity: false,
            },
        },
        // file downloaded: fraudulent IP. in allowed countries.
        {
            severity: 'warning',
            description: (fields) => `A suspicious IP within your allowed countries downloaded a file`,
            longDescription: (fields) =>
                `A file was downloaded from OneDrive. The action originated from a suspicious IP located within your allowed countries. While the country itself aligns with your policy, the specific IP has been flagged as suspicious. Since this involves a file download—a higher-risk action than page views—review the activity to confirm whether the access was legitimate or potentially unauthorized.`,
            predicates: {
                isAllowedCountry: true,
                isFraudulentIP: true,
                isMicrosoftActivity: false,
            },
        },
        // file downloaded: fraudulent IP. in allowed asn.
        {
            severity: 'warning',
            description: (fields) => `A suspicious IP within your allowed ASN list downloaded a file`,
            longDescription: (fields) =>
                `A file was downloaded from OneDrive. The action originated from a suspicious IP included in your allowed ASN list. Although the ASN itself is normally trusted, the individual IP has been flagged as suspicious, making the activity unusual. Review the download to confirm whether the access was legitimate or potentially unauthorized, particularly if the file contained sensitive data or if repeated downloads are observed from similar flagged sources.`,
            predicates: {
                isAllowedASN: true,
                isFraudulentIP: true,
                isMicrosoftActivity: false,
            },
        },
        // file downloaded: user in org. not in allowed list
        {
            severity: 'warning',
            description: (fields) =>
                `An unapproved IP outside your allowed ASN, IPs, region, or country downloaded a file`,
            longDescription: (fields) =>
                `A file was downloaded from OneDrive. The action originated from an unapproved IP outside all of your allowed ASNs, IPs, regions, and countries. This places the access attempt outside your defined trusted network and geographic boundaries. While this does not automatically indicate malicious behaviour, the activity is unusual and worth review — especially if the file contained sensitive data or if repeated access attempts from unapproved sources occur.`,
            predicates: {
                isAllowedASN: false,
                isAllowedIP: false,
                isAllowedRegion: false,
                isAllowedCountry: false,
                isMicrosoftActivity: false,
                isUserMemberOfOrganisation: true,
            },
        },
        // file downloaded: user not in org. not in allowed list
        {
            severity: 'info',
            description: (fields) => `An unapproved IP used a non-org account to download a file`,
            longDescription: (fields) =>
                `A file was downloaded from OneDrive with an account that does not belong to your organisation. The action originated from an unapproved IP outside your allowed list. While file downloads are routine, the use of an external account combined with an unapproved IP makes this activity unusual. Review the access details to confirm whether the download was expected or potentially unauthorized, especially if the file contained sensitive information.`,
            predicates: {
                isAllowedASN: false,
                isAllowedIP: false,
                isAllowedRegion: false,
                isAllowedCountry: false,
                isMicrosoftActivity: false,
                isUserMemberOfOrganisation: false,
            },
        },
        // file downloaded: microsoft activity
        {
            severity: 'info',
            description: (fields) => `A Microsoft system operation (synchronization server IP) downloaded a file`,
            longDescription: (fields) =>
                `A file was downloaded from OneDrive. This was logged as a Microsoft system operation event, with the IP address tied to the Microsoft server responsible for handling synchronization. This is an informational record of expected background activity. No action is required unless file downloads attributed to system processes appear inconsistent with your expected configuration or usage.`,
            predicates: {
                isMicrosoftActivity: true,
            },
        },
        // file downloaded
        {
            severity: 'info',
            description: (fields) => `A file was downloaded from OneDrive`,
            longDescription: (fields) =>
                `A file was downloaded from OneDrive. This is an informational record of a standard activity event. No unusual conditions were associated with this entry. No action is required unless the download was unexpected or coincides with other anomalies.`,
        },
    ],
};

export default FileDownloaded;
